import { useAppContext } from "../../context/AppContext";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Published from "../../assets/Published-Post.png";
import {
  getPlatformIcon,
  getPlatformIconBackgroundColors,
} from "../../utils/platformIcons";
import { Platform } from "../../types";
import { useTranslation } from "react-i18next";
import SocialPostSkeleton from "@/skeleton-1/dashboardSkeleton";

function RecentPosts({ post }: any) {
  const { state, postHistory } = useAppContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const allPosts = postHistory?.data || [];
  const socialPlatforms: Platform[] = [
    "facebook",
    "instagram",
    "linkedin",
    "tiktok",
    "youtube",
  ];

  const platformsWithData = useMemo(() => {
    const set = new Set<Platform>();
    for (const p of allPosts) {
      if (p?.platform) set.add(p.platform as Platform);
    }
    return set;
  }, [allPosts]);

  const platformsWithDataSet = useMemo(() => {
    return new Set<Platform>(platformsWithData);
  }, [platformsWithData]);

  const [selectedPlatform, setSelectedPlatform] = useState<Platform | any>(
    null
  );

  useEffect(() => {
    if (!allPosts?.length) return;

    if (platformsWithDataSet.has(selectedPlatform)) return;

    if (platformsWithDataSet.has("facebook")) {
      setSelectedPlatform("facebook");
      return;
    }

    const firstAvailablePlatform = [...platformsWithData].find((platform) =>
      platformsWithDataSet.has(platform)
    );
    if (firstAvailablePlatform) {
      setSelectedPlatform(firstAvailablePlatform);
    }
  }, [allPosts, platformsWithData, platformsWithDataSet, selectedPlatform]);

  const topPost = allPosts
    .filter((p: any) => p?.platform === selectedPlatform)
    .sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

  const {
    content = "",
    metadata: { title = "", description = "", image } = {},
  } = topPost || {};

  const ext = image?.split(".")?.pop()?.toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

  if (state.postHistoryLoading) {
    return <SocialPostSkeleton />;
  }

  return (
    <div className="bg-gray-100 rounded-md p-4 flex flex-col h-[450px] w-full relative">
      {!!allPosts?.length && (
        <div className="flex w-full justify-between gap-2 mb-3">
          {socialPlatforms.map((platform) => {
            const IconComponent = getPlatformIcon(platform);
            const isActive = selectedPlatform === platform;
            const hasData = platformsWithDataSet.has(platform);

            return (
              <button
                key={platform}
                type="button"
                disabled={!hasData}
                onClick={() => {
                  if (!hasData) return;
                  setSelectedPlatform(platform);
                }}
                className={`relative p-1 rounded-full transition-all duration-200 transform h-fit
                  ${hasData ? "hover:scale-105" : ""}
                  ${isActive && hasData ? "ring-4 ring-blue-200 shadow-md" : ""}
                  ${hasData ? "" : "opacity-30 cursor-not-allowed"}`}
                title={hasData ? platform : `${platform} (no data)`}
              >
                <div className="w-8 md:w-10 h-8 md:h-10 rounded-full flex items-center justify-center shadow-md">
                  {IconComponent ? (
                    <IconComponent className="w-7 md:w-7 h-7 md:h-7 shrink-0" />
                  ) : (
                    <span className="font-bold text-sm">
                      {platform.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>

                {isActive && hasData && (
                  <div className="absolute inset-0 rounded-full animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {!topPost ? (
        <div className="p-2 flex-1 flex flex-col">
          <div className="flex justify-center items-center h-[220px]">
            <img
              src={Published}
              alt="No posts"
              className="h-full w-auto object-contain"
            />
          </div>

          <div className="mt-2 text-left">
            <h3 className="text-lg font-semibold text-black">
              You haven’t published any posts.
            </h3>

            <p className="text-sm text-black mt-2 leading-relaxed">
              Click the button below to create your first post and start sharing
              content.
            </p>
          </div>
        </div>
      ) : (
        <div
          className={`relative flex-1 h-full shadow-md rounded-md 
          overflow-hidden flex flex-col
          ${isImage ? "bg-gray-900 text-white" : "bg-white text-black"}`}
        >
          {isImage && (
            <>
              <div
                className="absolute inset-0 bg-cover bg-top"
                style={{
                  backgroundImage: `url(${image})`,
                  filter: "brightness(0.3)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t " />
            </>
          )}

          <div className="relative z-10 flex flex-col h-full p-3">
            <h3
              className={`font-semibold text-lg mb-2 ${
                isImage && "drop-shadow-[0_1.5px_1.5px_rgba(0,0,0,0.8)]"
              } line-clamp-2 `}
            >
              {(selectedPlatform === "tiktok" ||
                selectedPlatform === "youtube") &&
                title}
            </h3>

            <p
              title={description || content}
              className={`${
                selectedPlatform === "tiktok" ||
                (selectedPlatform === "youtube" && "text-sm") ||
                "text-base"
              } mt-3 mb-1 leading-relaxed ${
                isImage && "drop-shadow-[0_1.5px_1.5px_rgba(0,0,0,0.8)]"
              } ${isImage ? "" : "text-gray-700"} line-clamp-5`}
            >
              {description || content || "No content available"}
            </p>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate("/content")}
        className="w-full text-white py-2 text-base font-semibold px-4 rounded-md text-md transition-all border border-[#7650e3] bg-[#7650e3] hover:bg-[#d7d7fc] hover:text-[#7650e3] hover:border-[#7650e3] mt-4"
      >
        {t("create_post")}
      </button>
    </div>
  );
}

export default RecentPosts;
